import React from 'react';
import { EnhancedEventCard } from '@eb/eds-event-card';

import { useUser, useLazyImages } from '../../hooks';
import {
    useSaveEvent,
    useSaveEventToCollection,
    useCreateCollection,
    useEventCollections,
} from './hooks';

import { EventCardEventData, EventCardWithHandlersProps } from './types';
import {
    trackEventClick,
    trackSaveEvent,
    trackShowCreateCollectionView,
    trackShowAddToCollectionView,
    trackSaveEventToCollection,
    trackCreateCollection,
} from './analytics';

export const EventCard: React.FunctionComponent<
    EventCardEventData & EventCardWithHandlersProps
> = (props) => {
    //Initialize document event listener for lazy loading images
    useLazyImages(props.isLazyImage);

    //Local state
    const [saved, setSaved] = React.useState(!!props.savedByYou);

    //Data fetchers
    const user = useUser({ user: props.user });
    const {
        eventCollections,
        refetch: fetchEventCollections,
    } = useEventCollections(props.id, user?.collections || [], {
        enabled: false,
    });

    //Action handlers and tracking helpers
    const handleSaveEvent = useSaveEvent({
        setSaved,
        onCompleted: () => {
            props.onSave?.(saved, props.id);

            props.gaCategory &&
                props.isAuthenticated &&
                trackSaveEvent(props.gaCategory, !saved, props.id);
        },
    });

    const handleSaveEventToCollection = useSaveEventToCollection({
        onCompleted: ({
            collectionId,
            isChecked,
        }: {
            collectionId: string;
            isChecked: boolean;
        }) => {
            trackSaveEventToCollection(
                props.gaCategory,
                isChecked,
                collectionId,
            );
            props.onSaveEventToCollection?.({
                isChecked,
                collectionId,
                eventId: props.id,
            });
        },
    });

    const handleCreateCollection = useCreateCollection({
        userId: user?.id || '',
        onCompleted: (collectionId: string) => {
            trackCreateCollection(props.gaCategory, collectionId);
            props.onCreateCollection?.(name);
        },
    });

    const handleOnClick = () => {
        props.gaCategory && trackEventClick(props.gaCategory, props.id);
        props.onClick?.(props.id);
    };

    const handleShowCreateCollection = () => {
        props.gaCategory && trackShowCreateCollectionView(props.gaCategory);
        props.onShowCreateCollectionView?.();
    };

    const handleShowAddToCollection = () => {
        props.gaCategory && trackShowAddToCollectionView(props.gaCategory);
        props.onShowAddToCollectionView?.();

        user.fetchUserData().then(() => {
            if (!eventCollections) {
                return fetchEventCollections();
            }
        });
    };

    return (
        <EnhancedEventCard
            {...props}
            onAddOrRemoveEventToCollection={(data: {
                isChecked: boolean;
                value: string;
            }) =>
                handleSaveEventToCollection({
                    isChecked: data.isChecked,
                    collectionId: data.value,
                    eventId: props.id,
                })
            }
            onClick={handleOnClick}
            onCreateCollection={(name: string) =>
                handleCreateCollection({ name, eventId: props.id })
            }
            onSaveEvent={() =>
                handleSaveEvent({
                    eventId: props.id,
                    isAuthenticated: !!props.isAuthenticated,
                    savedByYou: saved,
                })
            }
            onShowAddToCollectionView={handleShowAddToCollection}
            onShowCreateCollectionView={handleShowCreateCollection}
            savedByYou={saved}
            userCollections={user.collections || []}
            eventCollections={eventCollections || []}
        />
    );
};
